<template>
  <div class="relative min-h-screen bg-gradient-to-br from-gray-100 to-blue-50">

    <!-- Toggle Filter Button (top-left corner) -->
    <button @click="toggleFilter"
      class="fixed top-24 left-4 z-50 bg-white shadow-md rounded-full p-2 hover:bg-gray-100 h-12 w-12 flex flex-col justify-center items-center">
      <font-awesome-icon :icon="['fas', 'filter']" class="text-gray-700 text-xl" />
    </button>

    <!-- Sliding Sidebar Filter -->
    <transition name="slide">
      <aside v-show="showFilter" class="fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-40 flex flex-col border-r">
        <div class="p-6 border-b">
          <h2 class="text-xl font-bold text-gray-800 w-full flex items-center justify-center pt-20 mt-1">
            <font-awesome-icon :icon="['fas', 'sliders-h']" class="mr-2 text-blue-600" />
            Filters
          </h2>
        </div>
        <div class="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          <div v-if="isShowFilters">
            <label class="block text-sm font-semibold text-gray-700 mb-1">Entity</label>
            <select v-model="filterByEntity"
              class="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:outline-none text-sm">
              <option value="">All</option>
              <option :value="item.entity" v-for="(item, index) in filteredEntities" :key="index">{{
                getEntityName(item.entity) }}</option>
            </select>
          </div>
          <div v-if="isShowFilters">
            <label class="block text-sm font-semibold text-gray-700 mb-1">Employer Type</label>
            <select v-model="filterByEmployerType"
              class="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:outline-none text-sm">
              <option value="">All</option>
              <option value="registered">Registered Employer</option>
              <option value="non_registered">Non - Registered Employer</option>
            </select>
          </div>
          <div v-if="isShowFilters">
            <label class="block text-sm font-semibold text-gray-700 mb-1">Employer</label>
            <select v-model="filterByPartner"
              class="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:outline-none text-sm">
              <option value="">All Employers</option>
              <option :value="item.id" v-for="(item, index) in partnersList" :key="index">{{ item.en_company_name
              }}</option>
            </select>
          </div>
          <div v-if="isShowFilters">
            <label class="block text-sm font-semibold text-gray-700 mb-1">Sector</label>
            <select v-model="filterBySector"
              class="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:outline-none text-sm">
              <option value="">All</option>
              <option value="Government – Federal">Government – Federal</option>
              <option value="Government – Local">Government – Local</option>
              <option value="Semi-Government">Semi-Government</option>
              <option value="Private">Private</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Local or Non Local</label>
            <select v-model="filterByJobFor"
              class="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:outline-none text-sm">
              <option value="">All</option>
              <option value="1">Local</option>
              <option value="2">Non Local</option>
              <option value="3">Both Local and Non Local</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Applications</label>
            <select v-model="filterByJobApplications"
              class="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:outline-none text-sm">
              <option value="">All</option>
              <option value="1">Yes</option>
              <option value="0">No</option>
            </select>
          </div>
        </div>

        <!-- Apply Button -->
        <div class="p-6 flex justify-center space-x-6 border-t">
          <button @click.stop="clearFilters"
            class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold rounded-lg">
            Clear
          </button>
          <button @click.stop="applyFilters"
            class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold rounded-lg">
            Apply
          </button>
        </div>
      </aside>
    </transition>

    <div class="h-full container mx-auto">
      <div class="flex flex-col h-full w-full p-0 sm:p-8">
        <!-- Breadcrumb -->
        <nav class="text-sm text-gray-600 mb-6 flex pt-6 sm:pt-0 pl-4 sm:pl-0">
          <router-link to="/dashboard" class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
          <span class="mx-1">/</span>
          <router-link to="/sponsorship-management" class="hover:text-blue-700 transition font-medium">Sponsorship
          </router-link>
          <span class="mx-1">/</span>
          <span class="text-blue-900 font-semibold">Post Sponsorship</span>
        </nav>

        <!-- Page Title Card -->
        <div class="bg-white sm:rounded-3xl shadow-md p-4 sm:p-8 mb-4 sm:mb-6 sm:border border-gray-200">
          <h1 class="text-xl sm:text-3xl font-bold text-blue-900 flex items-center gap-3">
            <span>🎓</span>
            Post Sponsorship
          </h1>
          <p class="text-gray-500 mt-2 text-xs sm:text-sm">List and create sponsorship opportunities for active students
          </p>
        </div>

        <div class="flex justify-end items-center gap-4">
          <div v-if="hasPermission('jobs-export-excel')" class="flex justify-end items-center">
            <div v-if="isExportExcel"
              class="w-44 flex items-center justify-center hover:bg-purple-50 bg-ternary border-ternary hover:border-ternary border px-4 py-2 rounded-lg hover:text-ternary text-white">
              <base-spinner-with-text class-list="w-4 h-4" text=""
                text-class-list="text-white"></base-spinner-with-text>
              <span class="text-xs">Exporting Excel</span>
            </div>
            <div v-else @click="generateReport"
              class="w-44 flex items-center justify-center hover:bg-purple-50 bg-ternary border-ternary hover:border-ternary border px-4 py-2 rounded-lg cursor-pointer hover:text-ternary text-white">
              <font-awesome-icon :icon="['fas', 'file-excel']" />
              <span class="text-xs ml-2">Export Excel</span>
            </div>
          </div>
          <div v-if="hasPermission('jobs-create')" @click.stop="createModal = true"
            class="flex items-center bg-ternary hover:bg-white border-ternary border px-4 py-2 rounded-lg cursor-pointer text-white hover:text-ternary">
            <icon-plus class="w-4 h-4"></icon-plus>
            <span class="text-xs ml-2">Add</span>
          </div>
        </div>

        <section class="mt-4 h-full w-full flex flex-col bg-white rounded-2xl border shadow-lg p-6 md:p-8">
          <div class="flex justify-between flex-wrap gap-4 mb-4">
            <h1 class="text-base md:text-lg font-semibold">Sponsorship Opportunities List</h1>
            <div class="w-full sm:w-auto">
              <search @on-search="onSearch" class-list="px-4 h-8"></search>
            </div>
          </div>

          <div class="flex flex-col" v-if="loading">
            <table-shimmer :titleHeaders="[
              '#',
              'Entity',
              'Job Title',
              'Position',
              'Job Location',
              'Vacancy',
              'Applications',
              'Accept',
              'Salary',
              'Job Offer Start Date',
              'Job Offer Close Date',
              'Actions',
            ]" :rows="first"></table-shimmer>
          </div>
          <div v-else-if="!loading && !hasItems" class="h-full flex justify-center items-center">
            <h1 class="text-center text-xl font-medium">No jobs found!</h1>
          </div>
          <div v-else class="flex flex-col">
            <div class="flex flex-col overflow-x-auto">
              <div class="inline-block min-w-full py-2">
                <div class="overflow-x-auto">
                  <table class="min-w-full text-left text-sm font-light">
                    <thead class="border-b font-medium dark:border-neutral-500">
                      <tr>
                        <th scope="col" class="px-6 py-2 text-gray-400 w-8">
                          #
                        </th>
                        <th v-if="role == 'admin' || role == 'superadmin'" scope="col" class="px-6 py-2 text-gray-400">
                          Employer
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Entity
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Job Title
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Position
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Job Location
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Vacancy
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Applications
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Accept
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Salary Range
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Job Offer Start Date
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Job Offer Close Date
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Status
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Posted By
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in items" :key="index" :class="item.is_posted ? '' : 'bg-yellow-300'"
                        class="border-b border-dashed dark:border-neutral-500">
                        <td class="whitespace-nowrap px-6 py-2 font-medium">
                          {{ paginatorInfo.firstItem + index }}
                        </td>
                        <td v-if="role == 'admin' || role == 'superadmin'" class="whitespace-nowrap px-6 py-2">
                          <div class="flex items-center">
                            <div v-if="item.partner" class="flex-shrink-0 mr-3">
                              <img class="h-12 w-12 rounded-lg object-contain cursor-pointer"
                                :src="getSrcFile(item.partner.company_logo)" alt="" />
                            </div>
                            <div class="flex-grow flex flex-col mr-4">
                              <h1 class="text-sm font-semibold flex items-center">
                                <span class="text-heading hover:text-blue-600 hover:underline cursor-pointer">{{
                                  item.partner ? item.partner.en_company_name : item.employer_name }}</span>
                              </h1>
                              <p v-if="item.partner" class="text-xs text-gray-400">
                                {{ item.partner.ar_company_name }}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ getEntityName(item.entity) }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ item.job_title }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ item.position_name }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ item.job_location }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ item.vacancy }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          <span v-if="item.request_count" @click.stop="viewApplications(item.token)"
                            class="hover:underline hover:text-blue-600 cursor-pointer"
                            title="Click to view the applications">{{ item.request_count }}</span>
                          <span v-else>{{ item.request_count }}</span>
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          <span v-if="item.approved_count" @click.stop="showStudentsList(item.id, true)"
                            class="hover:underline hover:text-blue-600 cursor-pointer"
                            title="Click to view the students list">{{ item.approved_count }}</span>
                          <span v-else>{{ item.approved_count }}</span>
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ item.salary }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ formattedDate(item.job_start_date) }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ formattedDate(item.job_close_date) }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ item.is_posted ? 'Posted' : 'Not posted yet' }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ item.posted_by ? item.posted_by.name : item.created_by }}
                        </td>
                        <td class="px-6 py-2">
                          <div class="flex">
                            <div v-if="hasPermission('jobs-show')" @click.stop="viewItem(item)"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                              title="View Job">
                              <icon-eye
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-eye>
                            </div>
                            <div v-if="hasPermission('jobs-edit') && isEditableForMe(item.created_by_user_id)"
                              @click.stop="editItem(item)"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                              title="Edit Job">
                              <icon-pen
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-pen>
                            </div>
                            <div v-if="hasPermission('jobs-delete')" @click.stop="deleteItem(item.id)"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                              title="Delete Job">
                              <icon-trash
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-trash>
                            </div>
                            <div v-if="
                              hasPermission(
                                'job-request-and-approval-index'
                              ) && item.request_count
                            " @click.stop="viewApplications(item.token)"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                              title="View Job Applications">
                              <icon-document-list
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-document-list>
                            </div>
                            <div v-if="hasPermission('jobs-send-email') && item.is_posted"
                              @click.stop="openEmailContentModal(item.id)"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                              title="Send Job Opportunity Email">
                              <icon-envelope
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-envelope>
                            </div>
                            <div v-if="item.is_posted"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer">
                              <rsd-copy-to-clipboard
                                :text-to-copy="getJobOpportunityUrl(item.token)"></rsd-copy-to-clipboard>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div class="mt-4">
              <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
            </div>
          </div>

          <transition name="fade" mode="out-in">
            <base-full-modal v-if="createModal" :title="`Post Sponsorship Opportunity`" @close="closeModal">
              <create :partners-list="partnersList" :non-registered-employers-list="nonRegisteredEmployersList"
                @created="onCreated" @close="closeModal"></create>
            </base-full-modal>
          </transition>

          <transition name="fade" mode="out-in">
            <base-full-modal v-if="editModal" :title="`Edit Sponsorship Opportunity`" @close="closeModal">
              <edit @updated="onUpdated" @close="closeModal" :selected-item="selectedItem"></edit>
            </base-full-modal>
          </transition>

          <transition name="fade" mode="out-in">
            <base-full-modal v-if="isViewItem"
              :title="`View Job - ${selectedItem.job_title} (${selectedItem.employer_name})`" height=""
              @close="closeModal">
              <apply-job :item="selectedItem" user-type="partner" @close="closeModal"></apply-job>
            </base-full-modal>
          </transition>

          <transition name="fade" mode="out-in">
            <base-modal v-if="isShowJobAcceptedStudents" :title="`Job - #${selectedJobId} - Students List`" height=""
              @close="closeModal">
              <job-accepted-students :job-id="selectedJobId" :is-approved="isApproved"
                @close="closeModal"></job-accepted-students>
            </base-modal>
          </transition>

          <transition name="fade" mode="out-in">
            <base-modal v-if="showAddNote" :title="`Add Your Note`" @close="closeAddNote">
              <div class="flex flex-col">
                <label for="note" class="px-8 mt-4 text-sm ml-1">Message</label>
                <div class="h-32 px-8 rounded-xl mt-2">
                  <textarea v-model="note"
                    class="bg-white resize-none w-full h-full outline-none text-sm border p-4 rounded-lg"
                    placeholder="Enter your message here or leave this field blank."></textarea>
                </div>
                <div class="flex justify-end mt-4 mr-8">
                  <div @click.stop="sendEmail"
                    class="border-2 font-medium border-ternary text-white hover:text-ternary bg-ternary hover:bg-white rounded-lg px-5 py-2 uppercase text-sm cursor-pointer">
                    <font-awesome-icon :icon="['fas', 'paper-plane']" />
                    <span class="ml-2">Send Email</span>
                  </div>
                </div>
              </div>
            </base-modal>
          </transition>

        </section>
      </div>
    </div>
    <div class="h-full flex justify-center items-center" v-if="isLoading || isExportExcel">
      <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
    </div>
  </div>
</template>

<script>
import IconEye from "../../components/icons/IconEye.vue";
import IconPen from "../../components/icons/IconPen.vue";
import IconTrash from "../../components/icons/IconTrash.vue";
import IconPlus from "../../components/icons/IconPlus.vue";
import IconEnvelope from "../../components/icons/IconEnvelope.vue";
import IconDocumentList from "../../components/icons/IconDocumentList.vue";
import gql from "graphql-tag";
import Pagination from "../../components/shared/pagination.vue";
import TableShimmer from "../../components/shared/TableShimmer.vue";
import Create from "./create.vue";
import Edit from "./edit.vue";
import ApplyJob from "../employment/jobs/job-opportunities/apply-job.vue";
import JobAcceptedStudents from "../employment/jobs/job-accepted-students.vue";
import Search from "../../components/shared/search.vue";
import RsdCopyToClipboard from "../../components/shared/RsdCopyToClipboard.vue";
import xlsx from "xlsx/dist/xlsx.full.min.js";
export default {
  name: "post-sponsorship-index",
  components: {
    IconEye,
    IconPen,
    IconTrash,
    IconPlus,
    IconEnvelope,
    IconDocumentList,
    Pagination,
    TableShimmer,
    Create,
    Edit,
    ApplyJob,
    JobAcceptedStudents,
    Search,
    RsdCopyToClipboard,
  },
  data() {
    return {
      jobs: {
        data: [],
        paginatorInfo: {}
      },
      first: 10,
      page: 1,
      createModal: false,
      editModal: false,
      isViewItem: false,
      modalTitle: "",
      selectedItem: null,
      error: null,
      isShowJobAcceptedStudents: false,
      selectedJobId: null,
      isApproved: null,
      filterByEmployerType: "",
      filterByPartner: "",
      filterBySector: "",
      partnersList: [],
      nonRegisteredEmployersList: [],
      filterByEntity: "",
      entities: [],
      filterByJobFor: "",
      filterByJobApplications: "",
      isLoading: false,
      searchText: "",
      showAddNote: false,
      selectedId: null,
      note: "",
      isExportExcel: false,
      isApiCallRecurisive: false,
      generatedReport: [],
      loading: false,
      showFilter: false,
    };
  },
  watch: {
    jobs(newVal) {
      if (newVal.error) {
        if (newVal.error_code == 403) {
          this.$router.replace("/no-permission");
        } else if (newVal.error_code == 401) {
          this.$router.replace("/");
        } else {
          alert(newVal.message);
          this.$router.replace("/");
        }
      }
    },
    filterByEntity() {
      this.filterByEmployerType = "";
      this.filterByPartner = "";
      this.filterBySector = "";
      this.page = 1;
      this.getPartnersList();
    },
    page() {
      this.getSponsorshipOpportunities();
    },
    searchText() {
      this.page = 1;
      this.getSponsorshipOpportunities();
    },
  },
  computed: {
    items() {
      return this.jobs.data;
    },
    hasItems() {
      if (this.items && this.items.length > 0) {
        return true;
      } else {
        return false;
      }
    },
    paginatorInfo() {
      return this.jobs.paginatorInfo;
    },
    userId() {
      return this.$store.getters.userId;
    },
    role() {
      return this.$store.getters.role;
    },
    filteredEntities() {
      return this.entities.data;
    },
    isShowFilters() {
      return ['admin', 'superadmin', 'Viewer'].includes(this.role);
    }
  },
  created() {
    this.getSponsorshipOpportunities();
    if (this.role == "admin" || this.role == "superadmin") {
      this.getPartnersList();
      this.getNonRegisteredEmployersList();
    }
  },
  apollo: {
    entities() {
      return {
        query: gql`
          query GetEntityList {
            entities {
              data {
                entity
              }
            }
          }
        `,
        fetchPolicy: "cache-and-network",
      };
    },
  },
  methods: {
    toggleFilter() {
      this.showFilter = !this.showFilter;
    },
    applyFilters() {
      this.toggleFilter();
      this.page = 1;
      this.getSponsorshipOpportunities();
    },
    clearFilters() {
      this.showFilter = false;
      this.filterByEntity = "";
      this.filterByEmployerType = "";
      this.filterByPartner = "";
      this.filterBySector = "";
      this.filterByJobFor = "";
      this.filterByJobApplications = "";
      this.page = 1;
      this.getPartnersList();
      this.getSponsorshipOpportunities();
    },
    async getSponsorshipOpportunities() {
      this.loading = true;
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetAllJobs(
              $first: Int!
              $page: Int!
              $filter_by_employer_type: String
              $filter_by_partner: Int
              $entity: String
              $job_for: String
              $entity_industry: String
              $search: String
              $request_count: String
              $opportunity_type: String
            ) {
              jobs(
                first: $first
                page: $page
                filter_by_employer_type: $filter_by_employer_type
                filter_by_partner: $filter_by_partner
                entity: $entity
                job_for: $job_for
                entity_industry: $entity_industry
                search: $search
                request_count: $request_count
                opportunity_type: $opportunity_type
              ) {
                error
                error_code
                message
                data {
                  id
                  employer_type
                  partner_id
                  employer_name
                  token
                  grade_number
                  job_title
                  position_name
                  position_number
                  job_description
                  minimum_qualification
                  preferred_qualification
                  expected_skills
                  special_instructions
                  division
                  department
                  job_start_date
                  job_close_date
                  job_category
                  salary
                  vacancy
                  request_count
                  approved_count
                  rejected_count
                  benefits
                  job_location
                  working_hours
                  contract_type
                  job_for
                  student_status
                  year_of_study
                  program
                  gender
                  entity
                  campus
                  opportunity_type
                  created_by_user_id
                  is_posted
                  partner {
                    id
                    en_company_name
                    ar_company_name
                    company_logo
                  }
                  created_by
                  posted_by {
                    name
                  }
                }
                paginatorInfo {
                  count
                  currentPage
                  firstItem
                  hasMorePages
                  lastItem
                  lastPage
                  perPage
                  total
                }
              }
            }
          `,
          variables: {
            first: this.first,
            page: this.page,
            filter_by_employer_type: this.filterByEmployerType,
            filter_by_partner: parseInt(this.filterByPartner),
            entity: this.filterByEntity,
            job_for: this.filterByJobFor,
            entity_industry: this.filterBySector,
            search: this.searchText,
            request_count: this.filterByJobApplications,
            opportunity_type: 'Sponsorship',
          },
          fetchPolicy: "network",
        });
        this.jobs = data.jobs;
      } catch (error) {
        console.log(error);
        this.jobs = {
          error: true,
          message: error.message || "An error occurred",
        };
      } finally {
        this.loading = false;
      }
    },
    onSearch(searchText) {
      this.searchText = searchText;
    },
    getEntityName(entity) {
      return entity.replace(/ATHS/g, "ATS");
    },
    formattedDate(date) {
      if (date) {
        const parsedDate = new Date(date);
        return parsedDate
          .toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
          .replace(/\//g, "-");
      } else {
        return "";
      }
    },
    setPage(page) {
      this.page = page;
    },
    closeModal() {
      this.createModal = false;
      this.editModal = false;
      this.isViewItem = false;
      this.modalTitle = "";
      this.selectedItem = null;
      this.isShowJobAcceptedStudents = false;
      this.selectedJobId = null;
      this.isApproved = null;
    },
    showToast(message = "") {
      this.getSponsorshipOpportunities();
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 1000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", this.$swal.stopTimer);
          toast.addEventListener("mouseleave", this.$swal.resumeTimer);
        },
      });
      Toast.fire({
        icon: "success",
        title: message,
      });
      this.closeModal();
    },
    onCreated() {
      this.showToast("Job Created Successfully");
    },
    editItem(item) {
      this.selectedItem = item;
      this.editModal = true;
    },
    async deleteItem(id) {
      this.$swal
        .fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            const DELETE_MUTATION = gql`
              mutation DeleteJob($id: ID!) {
                deleteJob(id: $id) {
                  error
                  message
                }
              }
            `;

            try {
              await this.$apollo
                .mutate({
                  mutation: DELETE_MUTATION,
                  variables: {
                    id: id,
                  },
                })
                .then((data) => {
                  if (data.data.deleteJob.error) {
                    this.error = data.data.deleteJob.message;
                  }
                })
                .catch((error) => {
                  this.error = error.message;
                });
            } catch (error) {
              this.error = error.message || "Please try again.";
            }

            if (this.error === null) {
              this.showToast("Your job has been deleted.");
            } else {
              this.$swal
                .fire({
                  icon: "error",
                  title: "Oops...",
                  text: this.error,
                })
                .then((result) => {
                  if (result.value || result.dismiss) {
                    this.error = null;
                  }
                });
            }
          }
        });
    },
    onUpdated() {
      this.showToast("Job Updated Successfully");
    },
    hasPermission(crudName) {
      if (this.role == "superadmin") {
        return true;
      } else {
        const permissions = this.$store.getters.permissions;
        const permissionsArray = permissions.split(",");
        if (permissionsArray.includes(crudName)) {
          return true;
        } else {
          return false;
        }
      }
    },
    isEditableForMe(userId) {
      if (this.role == "superadmin" || this.role == 'partner') {
        return true;
      } else {
        if (this.userId == userId) {
          return true
        } else {
          return false;
        }
      }
    },
    showStudentsList(jobId, isApproved) {
      this.selectedJobId = jobId;
      this.isApproved = isApproved;
      this.isShowJobAcceptedStudents = true;
    },
    viewItem(item) {
      this.selectedItem = item;
      this.isViewItem = true;
    },
    getSrcFile(url) {
      return process.env.VUE_APP_PUBLIC_URL + url;
    },
    async getPartnersList() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetActivePartnersList($entity: String) {
              activePartners(entity: $entity) {
                data {
                  id
                  en_company_name
                }
              }
            }
          `,
          variables: {
            entity: this.filterByEntity,
          },
          fetchPolicy: "network-only",
        });
        this.partnersList = data.activePartners.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getNonRegisteredEmployersList() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetNonRegisteredEmployersList {
              getNonRegisteredEmployersList
            }
          `,
          fetchPolicy: "network-only",
        });
        this.nonRegisteredEmployersList = [...data.getNonRegisteredEmployersList, 'Other'];
      } catch (error) {
        console.log(error);
      }
    },
    openEmailContentModal(id) {
      this.selectedId = id;
      this.showAddNote = true;
    },
    closeAddNote() {
      this.showAddNote = false;
      this.note = "";
      this.selectedId = null;
    },
    async sendEmail() {
      this.showAddNote = false;
      this.isLoading = true;

      const SEND_EMAIL_MUTATION = gql`
        mutation JobSendEmail($id: ID!, $note: String) {
          jobSendEmail(id: $id, note: $note) {
            error
            message
          }
        }
      `;

      let error_msg = "";
      let success_msg = "";

      try {
        await this.$apollo
          .mutate({
            mutation: SEND_EMAIL_MUTATION,
            variables: {
              id: this.selectedId,
              note: this.note,
            },
          })
          .then((data) => {
            if (data.data.jobSendEmail.error) {
              error_msg = data.data.jobSendEmail.message;
            } else {
              success_msg = data.data.jobSendEmail.message;
            }
          })
          .catch((error) => {
            error_msg = error.message;
          });
      } catch (error) {
        error_msg = error.message || "Please try again.";
      }

      this.isLoading = false;

      if (error_msg == "") {
        this.$swal
          .fire({
            icon: "success",
            title: success_msg,
            text: "",
          })
          .then((result) => {
            if (result.value || result.dismiss) {
              this.isLoading = false;
            }
          });
      } else {
        this.$swal
          .fire({
            icon: "error",
            title: "Oops...",
            text: error_msg,
          })
          .then((result) => {
            if (result.value || result.dismiss) {
              this.isLoading = false;
            }
          });
      }
    },
    viewApplications(token) {
      this.$router.replace(`/employment/job-request-and-approval/${token}`);
    },
    getJobOpportunityUrl(token) {
      return process.env.VUE_APP_FRONTEND_URL + 'employment/job-opportunity/' + token;
    },
    async generateReport() {
      var page = 1;
      var limit = 200;
      this.isExportExcel = true;
      this.isApiCallRecurisive = true;
      while (this.isApiCallRecurisive) {
        const responseData = await this.fetchForExporting(page, limit);
        if (responseData.error) {
          this.$swal
            .fire({
              icon: "error",
              title: "Oops...",
              text: responseData.message,
            })
          break;
        }

        if (responseData.data.length > 0) {
          this.generatedReport.push(
            ...responseData.data.map((obj) => {
              return {
                employer: obj.employer_type == 'registered' ? obj.partner.en_company_name : obj.employer_name,
                entity: this.getEntityName(obj.entity),
                job_title: obj.job_title,
                position_name: obj.position_name,
                job_description: obj.job_description,
                job_location: obj.job_location,
                minimum_qualification: obj.minimum_qualification,
                preferred_qualification: obj.preferred_qualification,
                expected_skills: obj.expected_skills,
                special_instructions: obj.special_instructions,
                job_start_date: obj.job_start_date,
                job_close_date: obj.job_close_date,
                job_for: this.getJobForByNationality(obj.job_for),
                gender: this.getJobForByGender(obj.gender),
                salary: obj.salary,
                benefits: obj.benefits,
                contract_type: obj.contract_type,
                working_hours: obj.working_hours,
                department: obj.department,
                division: obj.division,
                job_category: obj.job_category,
                posted_by: obj.posted_by.name,
                vacancy: obj.vacancy,
                no_of_applications: obj.no_of_applications,
              };
            })
          );
          page++;
        } else {
          this.isApiCallRecurisive = false;
          break;
        }
      }

      const entityMap = {
        ADPOLY: "ADPOLY_SPONSORSHIP_OPPORTUNITIES",
        FCHS: "FCHS_SPONSORSHIP_OPPORTUNITIES",
        ATHS: "ATS_SPONSORSHIP_OPPORTUNITIES",
        ATS: "ATS_SPONSORSHIP_OPPORTUNITIES",
        ADVETI: "ADVETI_SPONSORSHIP_OPPORTUNITIES"
      };
      const reportName = entityMap[this.filterByEntity] || "SPONSORSHIP_OPPORTUNITIES";

      this.exportToExcel(reportName);
    },
    async fetchForExporting(pageNo = 1, limitSize = 50) {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query ExportJobs(
              $page: Int
              $limit: Int
              $filter_by_employer_type: String
              $filter_by_partner: Int
              $entity: String
              $job_for: String
              $entity_industry: String
              $search: String
              $request_count: String
              $opportunity_type: String
            ) {
              exportJobs(
                page: $page
                limit: $limit
                filter_by_employer_type: $filter_by_employer_type
                filter_by_partner: $filter_by_partner
                entity: $entity
                job_for: $job_for
                entity_industry: $entity_industry
                search: $search
                request_count: $request_count
                opportunity_type: $opportunity_type
              ) {
                error
                error_code
                message
                data {
                  employer_type
                  employer_name
                  entity
                  job_title
                  position_name
                  job_description
                  job_location
                  minimum_qualification
                  preferred_qualification
                  expected_skills
                  special_instructions
                  job_start_date
                  job_close_date
                  job_for
                  gender
                  salary
                  benefits
                  contract_type
                  working_hours
                  department
                  division
                  job_category
                  vacancy
                  no_of_applications
                  posted_by {
                    name
                  }
                  partner {
                    en_company_name
                  }
                }
              }
            }
          `,
          fetchPolicy: "network-only",
          variables: {
            page: pageNo,
            limit: limitSize,
            filter_by_employer_type: this.filterByEmployerType,
            filter_by_partner: parseInt(this.filterByPartner),
            entity: this.filterByEntity,
            job_for: this.filterByJobFor,
            entity_industry: this.filterBySector,
            search: this.searchText,
            request_count: this.filterByJobApplications,
            opportunity_type: 'Sponsorship',
          },
        });

        const responseData = data.exportJobs;
        return responseData;
      } catch (error) {
        console.log(error.message);
      }
    },
    exportToExcel(reportName) {
      const XLSX = xlsx;

      let header = [
        "EMPLOYER",
        "ENTITY",
        "JOB TITLE",
        "POSITION",
        "JOB DESCRIPTION",
        "JOB LOCATION",
        "MINIMUM QUALIFICATION",
        "PREFERED QUALIFICATION",
        "EXPECTED SKILLS",
        "SPECIAL INSTRUCTIONS",
        "JOB OFFER START DATE",
        "JOB OFFER CLOSE DATE",
        "JOB FOR (BY NATIONALITY)",
        "JOB FOR (BY GENDER)",
        "SALARY",
        "BENEFITS",
        "CONTRACT TYPE",
        "WORKING HOURS",
        "DEPARTMENT",
        "DIVISION",
        "JOB CATEGORY",
        "JOB POSTED BY",
        "VACANCY",
        "NO OF APPLICATIONS",
      ];

      const worksheet = XLSX.utils.json_to_sheet(this.generatedReport);
      const columnWidths = [];

      // Dynamically set headers in the worksheet
      for (let i = 0; i < header.length; i++) {
        const column = String.fromCharCode(65 + (i % 26)); // Generate column letters (A-Z)
        const row = Math.floor(i / 26); // Determine if the column extends beyond Z (AA, AB, etc.)
        const columnLetter = row > 0 ? String.fromCharCode(64 + row) + column : column; // Handle multi-letter columns
        worksheet[`${columnLetter}1`] = { v: header[i], t: "s" }; // Assign header value

        // Set a default width dynamically based on the header text length
        columnWidths.push({ width: Math.max(40, header[i].length + 5) }); // Ensure a minimum width of 40
      }

      // Assign dynamic column widths to the worksheet
      worksheet["!cols"] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      XLSX.writeFile(workbook, `${reportName}.xlsx`);

      this.generatedReport = [];
      this.isExportExcel = false;
      this.isApiCallRecurisive = false;
    },
    getJobForByNationality(value) {
      const map = {
        1: "Local",
        2: "Non Local",
        3: "Both Local and Non Local"
      };
      return map[value] || "";
    },
    getJobForByGender(value) {
      const map = {
        'M': 'Male',
        'F': 'Female'
      };
      return map[value] || 'Both Male & Female';
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}
</style>
